import { Box, Tooltip, Typography } from '@mui/material';

/**
 * Visual preview of the Payment Request signature area, showing where each
 * enabled workflow step's DocuSign tab lands on the printed form.
 *
 * The preview is a stylized recreation of the signature section of
 * `backend/src/templates/payment-request/form.html` — three signature boxes
 * (Prepared By / Reviewed By / Approved By with a GM/CEO split) plus the
 * "below box" anchor strip and the AP/CA Initials line at the bottom.
 *
 * Anchor coordinates are stored as ratios of the canvas (so the preview
 * scales cleanly with the container). For each enabled step the component
 * draws a colored rectangle at:
 *
 *   (anchor.xRatio * canvasWidth + step.xOffset,
 *    anchor.yRatio * canvasHeight + step.yOffset)
 *
 * sized roughly to a real DocuSign tab (signature ≈ 130×30 px, initial
 * ≈ 50×30 px). Hovering an overlay shows a tooltip with the step's role
 * and offsets, useful when several overlays stack.
 */

export interface PRPreviewStep {
    stepnumber: number;
    approverrole: string;
    tab_type: 'signature' | 'initial' | null;
    anchor_key: string | null;
    x_offset: number;
    y_offset: number;
    enabled: boolean;
}

interface AnchorPos {
    label: string;
    /** x position in canvas coordinates (0–1 ratio). */
    xRatio: number;
    /** y position in canvas coordinates (0–1 ratio). */
    yRatio: number;
}

// Canvas dimensions for the preview. The signature area only occupies the
// bottom third of the form, so we draw just that — easier to read than
// scaling the whole page.
const CANVAS_W = 360;
const CANVAS_H = 280;

// Coordinate map: each anchor's natural position on the form, given as a
// ratio of the canvas (0,0 = top-left, 1,1 = bottom-right). Hand-calibrated
// to match the layout in form.html.
const ANCHOR_MAP: Record<string, AnchorPos> = {
    '\\anchor_prepared_by\\':       { label: 'Prepared By',       xRatio: 0.16, yRatio: 0.30 },
    '\\anchor_below_prepared_by\\': { label: 'Below Prepared By', xRatio: 0.16, yRatio: 0.62 },
    '\\anchor_fm_sign\\':           { label: 'Reviewed By (FM)',  xRatio: 0.46, yRatio: 0.30 },
    '\\anchor_below_reviewed_by\\': { label: 'Below Reviewed By', xRatio: 0.46, yRatio: 0.62 },
    '\\anchor_gm_sign\\':           { label: 'GM',                xRatio: 0.78, yRatio: 0.30 },
    '\\anchor_ceo_sign\\':          { label: 'CEO',               xRatio: 0.92, yRatio: 0.30 },
    '\\anchor_ap_initials\\':       { label: 'AP Initials',       xRatio: 0.50, yRatio: 0.85 },
    '\\anchor_ca_initials\\':       { label: 'CA Initials',       xRatio: 0.78, yRatio: 0.85 },
};

// Step → color for overlay. Cycle through a pleasant palette.
const STEP_COLORS = [
    '#1f77b4', '#2ca02c', '#d62728', '#9467bd',
    '#ff7f0e', '#17becf', '#e377c2', '#7f7f7f',
];

const tabSize = (tabType: 'signature' | 'initial' | null) => {
    if (tabType === 'initial') return { w: 50, h: 24 };
    return { w: 130, h: 28 };
};

interface Props {
    steps: PRPreviewStep[];
    /** When set, highlight just this step (others render at lower opacity). */
    highlightedStep?: number;
}

export default function PRWorkflowFormPreview({
    steps,
    highlightedStep,
}: Props) {
    return (
        <Box
            sx={{
                position: 'sticky',
                top: 16,
                width: CANVAS_W + 32,
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Form preview
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
            >
                Tab positions on the printed Payment Request form. Hover an
                overlay for details. Disabled or anchorless steps are hidden.
            </Typography>
            <Box
                sx={{
                    position: 'relative',
                    width: CANVAS_W,
                    height: CANVAS_H,
                    border: '1px solid #c5cad1',
                    borderRadius: 1,
                    backgroundColor: '#fff',
                    backgroundImage:
                        'linear-gradient(to bottom, #fafafa 0%, #fafafa 100%)',
                    overflow: 'hidden',
                }}
            >
                {/* Three signature boxes side-by-side (mirrors .sig-section grid 1fr 1fr 1.4fr). */}
                <Box sx={previewBoxSx({ left: '4%',  top: '15%', width: '26%', height: '34%', label: 'Prepared by' })} />
                <Box sx={previewBoxSx({ left: '34%', top: '15%', width: '26%', height: '34%', label: 'Reviewed by' })} />
                <Box sx={previewBoxSx({ left: '64%', top: '15%', width: '32%', height: '34%', label: 'Approved by' })}>
                    {/* GM / CEO split inside the Approved By box. */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        borderTop: '1px solid transparent',
                    }}>
                        <Box sx={{
                            borderRight: '1px dashed #ccc',
                            display: 'flex', alignItems: 'flex-start',
                            justifyContent: 'center', pt: '14px',
                            fontSize: 9, color: '#003366', fontWeight: 600,
                        }}>GM</Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start',
                            justifyContent: 'center', pt: '14px',
                            fontSize: 9, color: '#003366', fontWeight: 600,
                        }}>CEO</Box>
                    </Box>
                </Box>

                {/* "Below box" zone — reserved space mirroring the .below-sig-anchors strip. */}
                <Box sx={{
                    position: 'absolute',
                    left: '4%', right: '4%', top: '52%', height: '15%',
                    borderBottom: '1px dashed #e5e7eb',
                }} />

                {/* AP / CA Initials line. */}
                <Box sx={{
                    position: 'absolute',
                    left: '4%', right: '4%', bottom: '4%',
                    display: 'flex', justifyContent: 'flex-end', gap: 3,
                    fontSize: 9, color: '#888',
                }}>
                    <span>AP Initials: ___________</span>
                    <span>CA Initials: ___________</span>
                </Box>

                {/* Step overlays — drawn last so they sit on top. */}
                {steps
                    .filter((s) => s.enabled && s.anchor_key && ANCHOR_MAP[s.anchor_key])
                    .map((s, idx) => {
                        const a = ANCHOR_MAP[s.anchor_key!];
                        const { w, h } = tabSize(s.tab_type);
                        const baseX = a.xRatio * CANVAS_W;
                        const baseY = a.yRatio * CANVAS_H;
                        // Mirror the DocuSign rule: tab's bottom-left at
                        // anchor's bottom-left → tab extends UP-RIGHT from
                        // the anchor. Positive yOffset moves DOWN
                        // (subtract from the y-up extension), positive
                        // xOffset moves RIGHT.
                        const tabLeft = baseX + s.x_offset;
                        const tabTop = baseY + s.y_offset - h;
                        const color = STEP_COLORS[idx % STEP_COLORS.length];
                        const dim =
                            highlightedStep != null &&
                            highlightedStep !== s.stepnumber;
                        return (
                            <Tooltip
                                key={`${s.stepnumber}-${s.approverrole}`}
                                title={`Step ${s.stepnumber}: ${s.approverrole} · ${s.tab_type ?? '?'} · offset (${s.x_offset}, ${s.y_offset}) · anchor ${a.label}`}
                                placement="top"
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: tabLeft,
                                        top: tabTop,
                                        width: w,
                                        height: h,
                                        borderRadius: 0.5,
                                        border: `2px solid ${color}`,
                                        backgroundColor: `${color}33`,
                                        opacity: dim ? 0.25 : 1,
                                        transition: 'opacity 120ms',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color,
                                        pointerEvents: 'auto',
                                        cursor: 'help',
                                        userSelect: 'none',
                                    }}
                                >
                                    {s.stepnumber}.{abbr(s.approverrole)}
                                </Box>
                            </Tooltip>
                        );
                    })}
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
            >
                Tip: numbers are step indices. Adjust X/Y offsets in the editor
                to nudge tabs into place; the preview updates live.
            </Typography>
        </Box>
    );
}

interface BoxOpts {
    left: string;
    top: string;
    width: string;
    height: string;
    label: string;
}
function previewBoxSx(opts: BoxOpts): any {
    return {
        position: 'absolute',
        left: opts.left,
        top: opts.top,
        width: opts.width,
        height: opts.height,
        border: '1px solid #003366',
        borderRadius: 0.5,
        backgroundColor: 'rgba(0, 51, 102, 0.02)',
        '&::before': {
            content: `"${opts.label}"`,
            position: 'absolute',
            top: 2,
            left: 4,
            fontSize: 8,
            color: '#003366',
            fontWeight: 600,
            letterSpacing: 0.5,
        },
    };
}

const ROLE_ABBR: Record<string, string> = {
    Initiator: 'INIT',
    ProcurementDirector: 'PD',
    APTeam: 'AP',
    ChiefAccountant: 'CA',
    FinanceManager: 'FM',
    PCDDirector: 'PCD',
    GeneralManager: 'GM',
    CEO: 'CEO',
};
function abbr(role: string): string {
    return ROLE_ABBR[role] ?? role.slice(0, 3).toUpperCase();
}
